/**
 * Section: Shop By Collection
 * Handles: hover image switching, tab UI, mobile touch, accessibility
 * Compatible with Shopify OS 2.0 + theme editor live updates
 */

(function () {
  class ShopByCollection {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;

      // Elements
      this.imageSlides = section.querySelectorAll('.shop-by-collection__image-slide');
      this.collectionItems = section.querySelectorAll('.shop-by-collection__item');
      this.tabs = section.querySelectorAll('.shop-by-collection__tab');
      this.hasTabAssignments = Array.from(this.collectionItems).some((item) => item.dataset.tab);

      // State
      this.activeImageId = 'default';
      this.hoverTimer = null;
      this.isMobile = window.matchMedia('(max-width: 749px)').matches;

      this.init();
    }

    init() {
      this.bindItemEvents();
      this.bindTabEvents();
      this.bindResizeObserver();

      this.applyInitialTabState();
    }

    // ── Image switching ──────────────────────────────────────

    showImage(blockId) {
      if (this.activeImageId === blockId) return;

      const next = this.section.querySelector(`[data-image-id="${blockId}"]`);
      if (!next) return;

      // Fade out all, fade in target
      this.imageSlides.forEach((slide) => {
        slide.classList.remove('is-active');
      });

      next.classList.add('is-active');
      this.activeImageId = blockId;
    }

    resetToDefault() {
      this.showImage('default');
      this.collectionItems.forEach((item) => item.classList.remove('is-active'));
    }

    // ── Collection item interactions ─────────────────────────

    bindItemEvents() {
      this.collectionItems.forEach((item) => {
        const blockId = item.dataset.blockId;
        const hasImage = item.dataset.hasImage === 'true';
        const link = item.querySelector('[data-item-link]');

        if (!hasImage || !blockId) return;

        // Desktop: hover
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.hoverTimer);
          this.activateItem(item, blockId);
        });

        item.addEventListener('mouseleave', () => {
          // Small delay before resetting — prevents flicker between items
          this.hoverTimer = setTimeout(() => {
            this.resetToDefault();
          }, 80);
        });

        // Mobile: tap to preview image (prevent navigation on first tap)
        if (link) {
          link.addEventListener('click', (e) => {
            if (this.isMobile && !item.classList.contains('is-active')) {
              e.preventDefault();
              this.collectionItems.forEach((i) => i.classList.remove('is-active'));
              this.activateItem(item, blockId);
            }
            // Second tap: let the link navigate normally
          });
        }

        // Keyboard accessibility
        item.addEventListener('focusin', () => {
          this.activateItem(item, blockId);
        });

        item.addEventListener('focusout', () => {
          this.hoverTimer = setTimeout(() => {
            if (!this.section.contains(document.activeElement)) {
              this.resetToDefault();
            }
          }, 100);
        });
      });

      // When mouse leaves the entire grid, reset
      const grid = this.section.querySelector('.shop-by-collection__grid');
      if (grid) {
        grid.addEventListener('mouseleave', () => {
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => {
            this.resetToDefault();
          }, 120);
        });

        grid.addEventListener('mouseenter', () => {
          clearTimeout(this.hoverTimer);
        });
      }
    }

    activateItem(item, blockId) {
      this.collectionItems.forEach((i) => i.classList.remove('is-active'));
      item.classList.add('is-active');
      this.showImage(blockId);
    }

    // ── Tab switching ─────────────────────────────────────────

    bindTabEvents() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          if (tab.classList.contains('is-active')) return;

          this.tabs.forEach((t) => {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');

          this.handleTabChange(tab);
        });
      });
    }

    handleTabChange(tab) {
      const tabValue = tab.dataset.tab ?? '0';
      this.section.dataset.activeTab = tabValue;
      this.updateItemsForTab(tabValue);

      // Emit custom event for potential external use
      this.section.dispatchEvent(
        new CustomEvent('shop-by-collection:tab-change', {
          bubbles: true,
          detail: { tab: tabValue, label: tab.textContent.trim() },
        })
      );
    }

    updateItemsForTab(tabValue) {
      if (!this.collectionItems.length) return;

      const normalizedTab = tabValue ?? '0';
      let targetItem = null;

      if (this.hasTabAssignments) {
        this.collectionItems.forEach((item) => {
          const matches = (item.dataset.tab ?? '0') === normalizedTab;
          item.hidden = !matches;
          item.classList.remove('is-active');
          if (matches && !targetItem) targetItem = item;
        });
      } else {
        // No per-item tab assignment found: just switch the highlighted item by tab index
        this.collectionItems.forEach((item) => {
          item.hidden = false;
          item.classList.remove('is-active');
        });
        const tabIndex = Number.isNaN(Number(normalizedTab)) ? 0 : Number(normalizedTab);
        targetItem = this.collectionItems[tabIndex] || this.collectionItems[0] || null;
      }

      if (targetItem && targetItem.dataset.blockId) {
        targetItem.classList.add('is-active');
        this.showImage(targetItem.dataset.blockId);
      } else {
        this.resetToDefault();
      }
    }

    applyInitialTabState() {
      const activeTab = this.section.querySelector('.shop-by-collection__tab.is-active') || this.tabs[0];
      if (activeTab) {
        this.section.dataset.activeTab = activeTab.dataset.tab ?? '0';
      }
      this.showImage('default');
    }

    // ── Responsive ───────────────────────────────────────────

    bindResizeObserver() {
      const mq = window.matchMedia('(max-width: 749px)');
      const handler = (e) => {
        this.isMobile = e.matches;
      };

      if (mq.addEventListener) {
        mq.addEventListener('change', handler);
      } else {
        // Safari < 14 fallback
        mq.addListener(handler);
      }
    }

    // ── Cleanup ───────────────────────────────────────────────

    destroy() {
      clearTimeout(this.hoverTimer);
    }
  }

  // ── Shopify OS 2.0 section lifecycle ─────────────────────

  const instances = new Map();

  function initSection(container) {
    const section = container.querySelector('[data-section-id]');
    if (!section) return;

    const id = section.dataset.sectionId;
    if (instances.has(id)) return;

    instances.set(id, new ShopByCollection(section));
  }

  function destroySection(container) {
    const section = container.querySelector('[data-section-id]');
    if (!section) return;

    const id = section.dataset.sectionId;
    if (instances.has(id)) {
      instances.get(id).destroy();
      instances.delete(id);
    }
  }

  // Init on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.shop-by-collection').forEach((el) => {
      initSection(el.closest('shopify-section') || el.parentElement);
    });
  });

  // Shopify theme editor events
  document.addEventListener('shopify:section:load', (e) => {
    initSection(e.target);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    destroySection(e.target);
  });

  document.addEventListener('shopify:section:reorder', () => {
    document.querySelectorAll('.shop-by-collection').forEach((el) => {
      initSection(el.closest('shopify-section') || el.parentElement);
    });
  });

  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    const blockId = block.dataset?.shopifyEditorBlock
      ? JSON.parse(block.dataset.shopifyEditorBlock).id
      : null;

    if (!blockId) return;

    const item = document.querySelector(`[data-block-id="${blockId}"]`);
    if (!item) return;

    const sectionEl = item.closest('[data-section-id]');
    if (!sectionEl) return;

    const instance = instances.get(sectionEl.dataset.sectionId);
    if (instance) {
      instance.activateItem(item, blockId);
    }
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const block = e.target;
    const sectionEl = block.closest('[data-section-id]');
    if (!sectionEl) return;

    const instance = instances.get(sectionEl.dataset.sectionId);
    if (instance) {
      instance.resetToDefault();
    }
  });
})();
